import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCircle2, AlertCircle, Info, Loader2 } from 'lucide-react';
import { useNotificationStore } from '../../store/notificationStore';

const typeToIcon: Record<string, { icon: typeof Bell; color: string }> = {
  charging_started: { icon: CheckCircle2, color: 'text-green-600 dark:text-green-400' },
  charging_complete: { icon: CheckCircle2, color: 'text-green-600 dark:text-green-400' },
  charging_failed: { icon: AlertCircle, color: 'text-red-600 dark:text-red-400' },
  charging_auto_stopped: { icon: AlertCircle, color: 'text-orange-600 dark:text-orange-400' },
  wallet_credited: { icon: CheckCircle2, color: 'text-green-600 dark:text-green-400' },
  wallet_debited: { icon: Info, color: 'text-blue-600 dark:text-blue-400' },
  low_balance_warning: { icon: AlertCircle, color: 'text-orange-600 dark:text-orange-400' },
  payment_confirmed: { icon: CheckCircle2, color: 'text-green-600 dark:text-green-400' },
  payment_failed: { icon: AlertCircle, color: 'text-red-600 dark:text-red-400' },
  welcome: { icon: Info, color: 'text-blue-600 dark:text-blue-400' },
  custom: { icon: Bell, color: 'text-blue-600 dark:text-blue-400' },
  promotional: { icon: Info, color: 'text-blue-600 dark:text-blue-400' },
  system_alert: { icon: AlertCircle, color: 'text-orange-600 dark:text-orange-400' },
};

function getRelativeTime(dateStr: string): string {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diff = now - date;

  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  return new Date(dateStr).toLocaleDateString();
}

function getBackgroundColor(type: string, read: boolean): string {
  if (read) return 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700';

  if (type.includes('failed') || type.includes('warning') || type === 'system_alert') {
    return 'bg-orange-50 dark:bg-orange-900/20 border-orange-100 dark:border-orange-800';
  }
  if (type.includes('credited') || type.includes('confirmed') || type.includes('complete') || type.includes('started')) {
    return 'bg-green-50 dark:bg-green-900/20 border-green-100 dark:border-green-800';
  }
  return 'bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800';
}

function NotificationsShimmer() {
  return (
    <div className="space-y-3">
      {[...Array(6)].map((_, i) => (
        <div
          key={i}
          className="relative overflow-hidden rounded-2xl p-4 border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800"
        >
          <div className="flex gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-full" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="w-2 h-2 bg-gray-200 dark:bg-gray-700 rounded-full flex-shrink-0 mt-1" />
              </div>
              <div className="space-y-1.5 mb-2">
                <div className="h-3.5 w-full bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-3.5 w-2/3 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
              <div className="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
          </div>
          <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/20 dark:via-white/5 to-transparent" />
        </div>
      ))}
    </div>
  );
}

export function NotificationsView() {
  const {
    notifications,
    unreadCount,
    isLoading,
    hasMore,
    fetchNotifications,
    loadMore,
    markAsRead,
    markAllAsRead,
    fetchUnreadCount,
  } = useNotificationStore();
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
  }, []);

  useEffect(() => {
    if (unreadCount > 0) {
      const timer = setTimeout(() => {
        markAllAsRead();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [unreadCount]);

  const handleNotificationTap = useCallback((notification: typeof notifications[0]) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
    const deepLink = notification.data?.deepLink;
    if (deepLink) {
      navigate(deepLink);
    }
  }, [markAsRead, navigate]);

  return (
    <div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-900 transition-colors">
      <div className="px-4 py-6">
        {/* Page Title with Logo */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
              <svg width="40" height="40" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="50" cy="50" r="45" stroke="#8bc43e" strokeWidth="7"/>
                <rect x="32" y="30" width="10" height="20" rx="1" fill="#8bc43e"/>
                <rect x="58" y="30" width="10" height="20" rx="1" fill="#8bc43e"/>
                <rect x="41" y="58" width="18" height="12" rx="1" fill="#8bc43e"/>
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Notifications</h2>
              {unreadCount > 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">{unreadCount} unread</p>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">All caught up!</p>
              )}
            </div>
          </div>
        </div>

        {/* Notifications List */}
        <div className="space-y-3 pb-6">
          {isLoading && notifications.length === 0 ? (
            <NotificationsShimmer />
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                <Bell className="w-8 h-8 text-gray-400 dark:text-gray-500" />
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-center">No notifications yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {notifications.map((notification) => {
                const iconConfig = typeToIcon[notification.type] || typeToIcon.custom;
                const Icon = iconConfig.icon;

                return (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationTap(notification)}
                    className={`rounded-2xl p-4 border transition-all duration-200 active:scale-98 cursor-pointer ${getBackgroundColor(
                      notification.type,
                      notification.isRead,
                    )}`}
                  >
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <Icon className={`w-5 h-5 ${iconConfig.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                            {notification.title}
                          </h3>
                          {!notification.isRead && (
                            <div className="w-2 h-2 bg-[#8bc43e] rounded-full flex-shrink-0 mt-1"></div>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 leading-relaxed">
                          {notification.body}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {getRelativeTime(notification.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Load more */}
              {hasMore && (
                <button
                  onClick={loadMore}
                  disabled={isLoading}
                  className="w-full py-3 text-sm text-[#8bc43e] font-medium disabled:opacity-50"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                  ) : (
                    'Load more'
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
